import React, { useState } from 'react';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Modal } from '../../components/ui/Modal';
import { ArrowLeft, Check, X, AlertCircle, FileText, Send, CheckCircle, XCircle, RotateCcw, AlertTriangle, ShieldAlert, Eye, Search } from 'lucide-react';
import { mockOnboardingApplications } from './OnboardingMasterView';
interface OnboardingApplicationDetailProps {
  applicationId: string;
  onBack: () => void;
  userRole: 'maker' | 'checker';
  userEmail: string;
  onStatusChange: (appId: string, newStatus: string, comment: string, actionBy: string) => void;
}
export function OnboardingApplicationDetail({
  applicationId,
  onBack,
  userRole,
  userEmail,
  onStatusChange
}: OnboardingApplicationDetailProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionType, setActionType] = useState<'submit' | 'approve' | 'reject' | 'discrepant' | 'revert'>('submit');
  const [comment, setComment] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const app = mockOnboardingApplications.find(a => a.id === applicationId) || mockOnboardingApplications[0];
  // Tabs WITHOUT Calculated Values
  const tabs = [{
    id: 'overview',
    label: 'Overview'
  }, {
    id: 'documents',
    label: 'Documents'
  }, {
    id: 'risk',
    label: 'Risk Rating'
  }, {
    id: 'tasdeek',
    label: 'Tasdeeq'
  }, {
    id: 'kyc',
    label: 'KYC & Documents'
  }, {
    id: 'rules',
    label: 'Passed Verifications'
  }, {
    id: 'logs',
    label: 'Activity Log'
  }];
  const handleAction = (type: 'submit' | 'approve' | 'reject' | 'discrepant' | 'revert') => {
    setActionType(type);
    setComment('');
    setShowActionModal(true);
  };
  const handleConfirmAction = () => {
    setIsProcessing(true);
    setTimeout(() => {
      let newStatus = app.status;
      if (actionType === 'submit') {
        newStatus = 'Submitted to Checker';
      } else if (actionType === 'approve') {
        newStatus = 'Approved';
      } else if (actionType === 'reject') {
        newStatus = 'Rejected';
      } else if (actionType === 'discrepant') {
        newStatus = 'Discrepant';
      } else if (actionType === 'revert') {
        newStatus = 'Reverted to Maker';
      }
      onStatusChange(applicationId, newStatus, comment, userEmail);
      setIsProcessing(false);
      setShowActionModal(false);
      onBack();
    }, 1500);
  };
  const canMakerAct = userRole === 'maker' && (app.status === 'Pending' || app.status === 'Reverted to Maker' || app.status === 'Discrepant');
  const canCheckerAct = userRole === 'checker' && app.status === 'Submitted to Checker';
  const getStatusBadgeColor = (status: string) => {
    const statusMap: Record<string, string> = {
      Pending: 'bg-blue-100 text-blue-800',
      'Submitted to Checker': 'bg-purple-100 text-purple-800',
      Discrepant: 'bg-orange-100 text-orange-800',
      'Reverted to Maker': 'bg-yellow-100 text-yellow-800',
      Approved: 'bg-green-100 text-green-800',
      Rejected: 'bg-red-100 text-red-800'
    };
    return statusMap[status] || 'bg-gray-100 text-gray-800';
  };
  const renderOverview = () => <div className="space-y-8 animate-in fade-in duration-300" data-id="element-1594">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1595">
        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1596">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2" data-id="element-1597">
            Application Data
          </h3>
          <dl className="grid grid-cols-2 gap-4" data-id="element-1598">
            <dt className="text-sm font-medium text-gray-500" data-id="element-1599">
              Application ID
            </dt>
            <dd className="text-sm text-gray-900 font-mono" data-id="element-1600">{app.id}</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1601">
              Application Date
            </dt>
            <dd className="text-sm text-gray-900" data-id="element-1602">{app.date}</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1603">Submitted By</dt>
            <dd className="text-sm text-gray-900" data-id="element-1604">{app.submittedBy}</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1605">Stage</dt>
            <dd className="text-sm text-gray-900" data-id="element-1606">{app.mechanismStage}</dd>
          </dl>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1607">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2" data-id="element-1608">
            Merchant Details
          </h3>
          <dl className="grid grid-cols-2 gap-4" data-id="element-1609">
            <dt className="text-sm font-medium text-gray-500" data-id="element-1610">Merchant ID</dt>
            <dd className="text-sm text-gray-900 font-mono" data-id="element-1611">
              {app.merchantId}
            </dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1612">Merchant Name</dt>
            <dd className="text-sm text-gray-900" data-id="element-1613">{app.merchantName}</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1614">Type</dt>
            <dd className="text-sm text-gray-900" data-id="element-1615">{app.merchantType}</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1616">Partner</dt>
            <dd className="text-sm text-gray-900" data-id="element-1617">{app.partnerType}</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1618">Contact</dt>
            <dd className="text-sm text-gray-900" data-id="element-1619">
              contact@{app.merchantName.toLowerCase().replace(/\s/g, '')}.com
            </dd>
          </dl>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1620">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2" data-id="element-1621">
            Business Information
          </h3>
          <dl className="grid grid-cols-2 gap-4" data-id="element-1622">
            <dt className="text-sm font-medium text-gray-500" data-id="element-1623">Business Type</dt>
            <dd className="text-sm text-gray-900" data-id="element-1624">Retail</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1625">
              Business Tenure
            </dt>
            <dd className="text-sm text-gray-900" data-id="element-1626">5+ Years</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1627">NTN</dt>
            <dd className="text-sm text-gray-900 font-mono" data-id="element-1628">1234567-8</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1629">Province</dt>
            <dd className="text-sm text-gray-900" data-id="element-1630">Punjab</dd>
          </dl>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1631">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 border-b pb-2" data-id="element-1632">
            Bank Information
          </h3>
          <dl className="grid grid-cols-2 gap-4" data-id="element-1633">
            <dt className="text-sm font-medium text-gray-500" data-id="element-1634">Bank Name</dt>
            <dd className="text-sm text-gray-900" data-id="element-1635">HBL</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1636">
              Account Number
            </dt>
            <dd className="text-sm text-gray-900 font-mono" data-id="element-1637">**** 4589</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1638">Account Title</dt>
            <dd className="text-sm text-gray-900" data-id="element-1639">{app.merchantName}</dd>
            <dt className="text-sm font-medium text-gray-500" data-id="element-1640">IBAN</dt>
            <dd className="text-sm text-gray-900 font-mono" data-id="element-1641">
              PK36HABB****4589
            </dd>
          </dl>
        </div>
      </div>
    </div>;
  const renderKYC = () => <div className="space-y-8 animate-in fade-in duration-300" data-id="element-1642">
      <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm" data-id="element-1643">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-1644">
          KYC Verification
        </h3>
        <div className="flex items-center space-x-4 mb-6" data-id="element-1645">
          <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center" data-id="element-1646">
            <Check className="h-6 w-6 text-green-600" data-id="element-1647" />
          </div>
          <div data-id="element-1648">
            <p className="text-lg font-medium text-gray-900" data-id="element-1649">KYC Verified</p>
            <p className="text-sm text-gray-500" data-id="element-1650">
              All required documents have been approved.
            </p>
          </div>
        </div>
        <div className="space-y-3" data-id="element-1651">
          {['CNIC Front', 'CNIC Back', 'Utility Bill', 'Shop Photo', 'Bank Statement'].map(doc => <div key={doc} className="flex items-center justify-between p-3 bg-gray-50 rounded-md border border-gray-100" data-id="element-1652">
              <div className="flex items-center" data-id="element-1653">
                <FileText className="h-5 w-5 text-gray-400 mr-3" data-id="element-1654" />
                <span className="text-sm font-medium text-gray-700" data-id="element-1655">{doc}</span>
              </div>
              <div className="flex items-center space-x-3" data-id="element-1656">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1657">
                  Verified
                </span>
                <Button size="sm" variant="ghost" data-id="element-1658">
                  View
                </Button>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
  const renderDocuments = () => {
    const submittedDocs = [{
      name: 'CNIC Front Copy',
      date: '2024-01-12',
      status: 'Submitted',
      type: 'Identity'
    }, {
      name: 'CNIC Back Copy',
      date: '2024-01-12',
      status: 'Submitted',
      type: 'Identity'
    }, {
      name: 'Utility Bill',
      date: '2024-01-13',
      status: 'Submitted',
      type: 'Address Proof'
    }, {
      name: 'Bank Statement (3 months)',
      date: '2024-01-13',
      status: 'Submitted',
      type: 'Financial'
    }, {
      name: 'Business Registration Certificate',
      date: '2024-01-14',
      status: 'Submitted',
      type: 'Business'
    }, {
      name: 'Tax Return (Last Year)',
      date: '2024-01-14',
      status: 'Pending',
      type: 'Financial'
    }, {
      name: 'Shop Premises Photo',
      date: '2024-01-14',
      status: 'Submitted',
      type: 'Business'
    }];
    const statusColors: Record<string, string> = {
      Submitted: 'bg-blue-100 text-blue-800',
      Pending: 'bg-yellow-100 text-yellow-800',
      Rejected: 'bg-red-100 text-red-800'
    };
    const typeColors: Record<string, string> = {
      Identity: 'bg-purple-50 text-purple-700',
      'Address Proof': 'bg-teal-50 text-teal-700',
      Financial: 'bg-green-50 text-green-700',
      Business: 'bg-orange-50 text-orange-700'
    };
    return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-1659">
        <div className="flex items-center justify-between" data-id="element-1660">
          <div data-id="element-1661">
            <h3 className="text-lg font-semibold text-gray-900" data-id="element-1662">
              Submitted Documents
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1663">
              Raw documents submitted by the applicant during the onboarding
              process.
            </p>
          </div>
          <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full" data-id="element-1664">
            {submittedDocs.length} documents
          </span>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1665">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1666">
            <thead className="bg-gray-50" data-id="element-1667">
              <tr data-id="element-1668">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1669">
                  Document Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1670">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1671">
                  Submission Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1672">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1673">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1674">
              {submittedDocs.map(doc => <tr key={doc.name} className="hover:bg-gray-50 transition-colors" data-id="element-1675">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1676">
                    <div className="flex items-center" data-id="element-1677">
                      <FileText className="h-4 w-4 text-gray-400 mr-3 flex-shrink-0" data-id="element-1678" />
                      <span className="text-sm font-medium text-gray-900" data-id="element-1679">
                        {doc.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1680">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${typeColors[doc.type] || 'bg-gray-100 text-gray-700'}`} data-id="element-1681">
                      {doc.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1682">
                    {doc.date}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1683">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors[doc.status] || 'bg-gray-100 text-gray-800'}`} data-id="element-1684">
                      {doc.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-1685">
                    <Button size="sm" variant="ghost" className="text-blue-600 hover:text-blue-700 hover:bg-blue-50" data-id="element-1686">
                      <Eye className="h-3.5 w-3.5 mr-1.5" data-id="element-1687" />
                      View
                    </Button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3" data-id="element-1688">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1689" />
          <p className="text-sm text-amber-800" data-id="element-1690">
            These are raw submitted documents only. For verified KYC documents,
            refer to the <strong data-id="element-1691">KYC & Documents</strong> tab.
          </p>
        </div>
      </div>;
  };
  const renderRiskRating = () => {
    const riskScore = 53.1;
    const riskCategory = riskScore <= 30 ? 'Low' : riskScore <= 60 ? 'Medium' : 'High';
    const riskColor = riskCategory === 'Low' ? {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-800',
      badge: 'bg-green-100 text-green-800',
      bar: 'bg-green-500'
    } : riskCategory === 'Medium' ? {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-800',
      badge: 'bg-yellow-100 text-yellow-800',
      bar: 'bg-yellow-500'
    } : {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      badge: 'bg-red-100 text-red-800',
      bar: 'bg-red-500'
    };
    const nactaScores = {
      rows: [{
        metric: 'Father Name Score',
        weightage: '20%',
        score: '84.0'
      }, {
        metric: 'CNIC Score',
        weightage: '40%',
        score: '62.0'
      }, {
        metric: 'Name Score',
        weightage: '30%',
        score: '58.0'
      }],
      total: '53.1'
    };
    const unscScores = {
      rows: [{
        metric: 'CNIC Score',
        weightage: '40%',
        score: '47.0'
      }, {
        metric: 'Name Score',
        weightage: '30%',
        score: '40.0'
      }, {
        metric: 'DOB Score',
        weightage: '10%',
        score: '60.0'
      }],
      total: '29.44'
    };
    const renderScoreTable = (title: string, data: {
      rows: {
        metric: string;
        weightage: string;
        score: string;
      }[];
      total: string;
    }) => <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1692">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center space-x-2" data-id="element-1693">
          <Search className="h-4 w-4 text-blue-500" data-id="element-1694" />
          <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider" data-id="element-1695">
            {title}
          </h3>
        </div>
        <table className="min-w-full divide-y divide-gray-200" data-id="element-1696">
          <thead className="bg-gray-50" data-id="element-1697">
            <tr data-id="element-1698">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1699">
                Metric
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1700">
                Weightage
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1701">
                Score
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-1702">
            {data.rows.map(row => <tr key={row.metric} className="hover:bg-gray-50 transition-colors" data-id="element-1703">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1704">
                  {row.metric}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-600 tabular-nums" data-id="element-1705">
                  {row.weightage}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 tabular-nums" data-id="element-1706">
                  {row.score}
                </td>
              </tr>)}
            <tr className="bg-gray-50" data-id="element-1707">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900" data-id="element-1708">
                Total Score
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-400 tabular-nums" data-id="element-1709">
                –
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold text-gray-900 tabular-nums" data-id="element-1710">
                {data.total}
              </td>
            </tr>
          </tbody>
        </table>
      </div>;
    return <div className="space-y-6 animate-in fade-in duration-300" data-id="element-1711">
        <div className={`${riskColor.bg} border ${riskColor.border} rounded-lg p-6`} data-id="element-1712">
          <div className="flex items-center justify-between mb-4" data-id="element-1713">
            <div className="flex items-center space-x-4" data-id="element-1714">
              <div className={`h-16 w-16 rounded-full ${riskColor.badge} flex items-center justify-center flex-shrink-0`} data-id="element-1715">
                <ShieldAlert className={`h-8 w-8 ${riskColor.text}`} data-id="element-1716" />
              </div>
              <div data-id="element-1717">
                <p className="text-sm font-medium text-gray-500" data-id="element-1718">
                  Overall Risk Category
                </p>
                <p className={`text-3xl font-bold ${riskColor.text}`} data-id="element-1719">
                  {riskCategory} Risk
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-1720">
              <p className="text-sm font-medium text-gray-500" data-id="element-1721">Risk Score</p>
              <p className={`text-4xl font-bold ${riskColor.text}`} data-id="element-1722">
                {riskScore.toFixed(1)}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1723">out of 100</p>
            </div>
          </div>
          <div className="mt-2" data-id="element-1724">
            <div className="flex justify-between text-xs text-gray-500 mb-1" data-id="element-1725">
              <span data-id="element-1726">Low (0–30)</span>
              <span data-id="element-1727">Medium (31–60)</span>
              <span data-id="element-1728">High (61–100)</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 relative overflow-hidden" data-id="element-1729">
              <div className="absolute inset-0 flex" data-id="element-1730">
                <div className="h-full bg-green-200" style={{
                width: '30%'
              }} data-id="element-1731" />
                <div className="h-full bg-yellow-200" style={{
                width: '30%'
              }} data-id="element-1732" />
                <div className="h-full bg-red-200" style={{
                width: '40%'
              }} data-id="element-1733" />
              </div>
              <div className={`absolute top-0 left-0 h-full ${riskColor.bar} rounded-full transition-all`} style={{
              width: `${riskScore}%`
            }} data-id="element-1734" />
            </div>
          </div>

          {/* Age Scoring Factor */}
          <div className="mt-5 pt-4 border-t border-gray-200/70" data-id="element-1735">
            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2" data-id="element-1736">
              Age Scoring Factor
            </p>
            <div className="flex flex-wrap gap-2" data-id="element-1737">
              {[{
              band: 'Under 18',
              delta: -10
            }, {
              band: '18 – 25',
              delta: -5
            }, {
              band: '26 – 45',
              delta: 10
            }, {
              band: '46 – 65',
              delta: 5
            }, {
              band: '65+',
              delta: 0
            }].map(item => <span key={item.band} className="inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-gray-200 px-3 py-1 text-xs" data-id="element-1738">
                  <span className="font-medium text-gray-700" data-id="element-1739">{item.band}</span>
                  <span className={`font-semibold ${item.delta > 0 ? 'text-green-600' : item.delta < 0 ? 'text-red-600' : 'text-gray-500'}`} data-id="element-1740">
                    {item.delta === 0 ? 'No change' : `${item.delta > 0 ? '+' : ''}${item.delta}`}
                  </span>
                </span>)}
            </div>
          </div>
        </div>
        {renderScoreTable('NACTA Scores', nactaScores)}
        {renderScoreTable('UNSC Scores', unscScores)}
        <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start space-x-3" data-id="element-1741">
          <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-1742" />
          <p className="text-sm text-blue-700" data-id="element-1743">
            This is a read-only risk assessment. Risk scores are calculated
            automatically and do not affect verification outcomes.
          </p>
        </div>
      </div>;
  };
  const renderRules = () => <div className="space-y-6 animate-in fade-in duration-300" data-id="element-1744">
      <div className="flex items-center space-x-4 bg-green-50 p-6 rounded-lg border border-green-100" data-id="element-1745">
        <div className="h-16 w-16 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0" data-id="element-1746">
          <Check className="h-8 w-8 text-green-600" data-id="element-1747" />
        </div>
        <div data-id="element-1748">
          <h3 className="text-xl font-bold text-green-900" data-id="element-1749">
            Rule Engine Passed
          </h3>
          <p className="text-green-700" data-id="element-1750">
            Application meets all automated criteria for onboarding.
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-1751">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-1752">
          <thead className="bg-gray-50" data-id="element-1753">
            <tr data-id="element-1754">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1755">
                Rule Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1756">
                Condition
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1757">
                Result
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-1758">
            <tr data-id="element-1759">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1760">
                CNIC Verification
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1761">
                NADRA Verified
              </td>
              <td className="px-6 py-4 whitespace-nowrap" data-id="element-1762">
                <span className="text-green-600 font-medium flex items-center" data-id="element-1763">
                  <Check className="h-4 w-4 mr-1" data-id="element-1764" /> Pass
                </span>
              </td>
            </tr>
            <tr data-id="element-1765">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1766">
                AML Check
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1767">
                Not on blacklist
              </td>
              <td className="px-6 py-4 whitespace-nowrap" data-id="element-1768">
                <span className="text-green-600 font-medium flex items-center" data-id="element-1769">
                  <Check className="h-4 w-4 mr-1" data-id="element-1770" /> Pass
                </span>
              </td>
            </tr>
            <tr data-id="element-1771">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1772">
                Business Verification
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1773">
                Valid NTN
              </td>
              <td className="px-6 py-4 whitespace-nowrap" data-id="element-1774">
                <span className="text-green-600 font-medium flex items-center" data-id="element-1775">
                  <Check className="h-4 w-4 mr-1" data-id="element-1776" /> Pass
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>;
  const renderLogs = () => <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden animate-in fade-in duration-300" data-id="element-1777">
      <table className="min-w-full divide-y divide-gray-200" data-id="element-1778">
        <thead className="bg-gray-50" data-id="element-1779">
          <tr data-id="element-1780">
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1781">
              Date/Time
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1782">
              Action
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1783">
              Action By
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1784">
              Comments
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1785">
          <tr data-id="element-1786">
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1787">
              2024-01-15 14:30
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1788">
              Submitted to Checker
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1789">
              maker@bunyaadfinance.com
            </td>
            <td className="px-6 py-4 text-sm text-gray-500" data-id="element-1790">
              All documents verified and validated.
            </td>
          </tr>
          <tr data-id="element-1791">
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1792">
              2024-01-15 10:15
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1793">
              KYC Verified
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1794">
              system
            </td>
            <td className="px-6 py-4 text-sm text-gray-500" data-id="element-1795">
              Automated eKYC check passed.
            </td>
          </tr>
          <tr data-id="element-1796">
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1797">
              2024-01-14 09:00
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1798">
              Application Created
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1799">
              portal_api
            </td>
            <td className="px-6 py-4 text-sm text-gray-500" data-id="element-1800">
              Submitted via Merchant Portal.
            </td>
          </tr>
        </tbody>
      </table>
    </div>;
  const getActionModalContent = () => {
    switch (actionType) {
      case 'submit':
        return {
          title: 'Submit to Checker',
          description: 'Submit this onboarding application to the checker for review.',
          icon: <Send className="h-6 w-6 text-blue-600" data-id="element-1801" />,
          iconBg: 'bg-blue-100',
          buttonText: 'Submit to Checker',
          buttonVariant: 'primary' as const
        };
      case 'approve':
        return {
          title: 'Approve Application',
          description: 'Approve this onboarding application.',
          icon: <CheckCircle className="h-6 w-6 text-green-600" data-id="element-1802" />,
          iconBg: 'bg-green-100',
          buttonText: 'Approve Application',
          buttonVariant: 'primary' as const
        };
      case 'reject':
        return {
          title: 'Reject Application',
          description: 'Reject this onboarding application. Please provide a reason.',
          icon: <XCircle className="h-6 w-6 text-red-600" data-id="element-1803" />,
          iconBg: 'bg-red-100',
          buttonText: 'Reject Application',
          buttonVariant: 'secondary' as const
        };
      case 'discrepant':
        return {
          title: 'Mark as Discrepant',
          description: 'Mark this application as having discrepancies that need to be resolved.',
          icon: <AlertTriangle className="h-6 w-6 text-orange-600" data-id="element-1804" />,
          iconBg: 'bg-orange-100',
          buttonText: 'Mark Discrepant',
          buttonVariant: 'secondary' as const
        };
      case 'revert':
        return {
          title: 'Revert to Maker',
          description: 'Send this application back to the maker for corrections.',
          icon: <RotateCcw className="h-6 w-6 text-yellow-600" data-id="element-1805" />,
          iconBg: 'bg-yellow-100',
          buttonText: 'Revert to Maker',
          buttonVariant: 'secondary' as const
        };
    }
  };
  const modalContent = getActionModalContent();
  return <div className="space-y-6" data-id="element-1806">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-1807">
        <div className="flex items-center space-x-4" data-id="element-1808">
          <button onClick={onBack} className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors" data-id="element-1809">
            <ArrowLeft className="h-5 w-5" data-id="element-1810" />
          </button>
          <div data-id="element-1811">
            <div className="flex items-center space-x-3" data-id="element-1812">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-1813">{app.id}</h1>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(app.status)}`} data-id="element-1814">
                {app.status}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1815">{app.merchantName}</p>
          </div>
        </div>
        <div className="flex space-x-3" data-id="element-1816">
          {canMakerAct && <>
              <Button variant="secondary" onClick={() => handleAction('reject')} data-id="element-1817">
                <X className="h-4 w-4 mr-2" data-id="element-1818" />
                Reject
              </Button>
              <Button onClick={() => handleAction('submit')} data-id="element-1819">
                <Send className="h-4 w-4 mr-2" data-id="element-1820" />
                Submit to Checker
              </Button>
            </>}
          {canCheckerAct && <>
              <Button variant="secondary" onClick={() => handleAction('revert')} data-id="element-1821">
                <RotateCcw className="h-4 w-4 mr-2" data-id="element-1822" />
                Revert to Maker
              </Button>
              <Button variant="secondary" onClick={() => handleAction('discrepant')} data-id="element-1823">
                <AlertTriangle className="h-4 w-4 mr-2" data-id="element-1824" />
                Mark Discrepant
              </Button>
              <Button variant="secondary" onClick={() => handleAction('reject')} data-id="element-1825">
                <X className="h-4 w-4 mr-2" data-id="element-1826" />
                Reject
              </Button>
              <Button onClick={() => handleAction('approve')} data-id="element-1827">
                <Check className="h-4 w-4 mr-2" data-id="element-1828" />
                Approve
              </Button>
            </>}
          {!canMakerAct && !canCheckerAct && <div className="text-sm text-gray-500 bg-gray-50 px-4 py-2 rounded-lg" data-id="element-1829">
              No actions available for current status
            </div>}
        </div>
      </div>

      {/* User Role Indicator */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-1830">
        <div className="flex items-center justify-between" data-id="element-1831">
          <div className="flex items-center space-x-3" data-id="element-1832">
            <AlertCircle className="h-5 w-5 text-blue-600" data-id="element-1833" />
            <div data-id="element-1834">
              <p className="text-sm font-medium text-blue-900" data-id="element-1835">
                Logged in as:{' '}
                {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
              </p>
              <p className="text-xs text-blue-700" data-id="element-1836">{userEmail}</p>
            </div>
          </div>
          {canMakerAct && <span className="text-xs text-blue-700" data-id="element-1837">
              You can submit or reject this application
            </span>}
          {canCheckerAct && <span className="text-xs text-blue-700" data-id="element-1838">
              You can approve, reject, mark discrepant, or revert this
              application
            </span>}
        </div>
      </div>

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} data-id="element-1839" />

      {/* Content */}
      <div className="min-h-[500px]" data-id="element-1840">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'documents' && renderDocuments()}
        {activeTab === 'risk' && renderRiskRating()}
        {activeTab === 'tasdeek' && <div className="flex flex-col items-center justify-center min-h-[400px] text-center animate-in fade-in duration-300" data-id="element-1841">
            <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mb-4" data-id="element-1842">
              <FileText className="h-8 w-8 text-gray-400" data-id="element-1843" />
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2" data-id="element-1844">
              Tasdeeq
            </h3>
            <p className="text-sm text-gray-400 max-w-xs" data-id="element-1845">
              This section is reserved for future functionality and will be
              developed in a later phase.
            </p>
          </div>}
        {activeTab === 'kyc' && renderKYC()}
        {activeTab === 'rules' && renderRules()}
        {activeTab === 'logs' && renderLogs()}
      </div>

      {/* Action Modal */}
      <Modal isOpen={showActionModal} onClose={() => setShowActionModal(false)} title={modalContent.title} data-id="element-1846">
        <div className="space-y-4" data-id="element-1847">
          <div className="flex items-start space-x-4" data-id="element-1848">
            <div className={`h-12 w-12 rounded-full ${modalContent.iconBg} flex items-center justify-center flex-shrink-0`} data-id="element-1849">
              {modalContent.icon}
            </div>
            <div className="flex-1" data-id="element-1850">
              <p className="text-sm text-gray-600" data-id="element-1851">
                {modalContent.description}
              </p>
              <div className="mt-2 text-sm text-gray-500" data-id="element-1852">
                <p data-id="element-1853">
                  <strong data-id="element-1854">Application:</strong> {app.id}
                </p>
                <p data-id="element-1855">
                  <strong data-id="element-1856">Merchant:</strong> {app.merchantName}
                </p>
              </div>
            </div>
          </div>

          <div data-id="element-1857">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1858">
              Comments{' '}
              {(actionType === 'reject' || actionType === 'discrepant' || actionType === 'revert') && <span className="text-red-500" data-id="element-1859">*</span>}
            </label>
            <textarea value={comment} onChange={e => setComment(e.target.value)} rows={4} className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={actionType === 'reject' || actionType === 'discrepant' || actionType === 'revert' ? 'Please provide a reason...' : 'Add any comments or notes (optional)...'} data-id="element-1860" />
          </div>

          <div className="flex justify-end space-x-3 pt-4" data-id="element-1861">
            <Button variant="secondary" onClick={() => setShowActionModal(false)} data-id="element-1862">
              Cancel
            </Button>
            <Button variant={modalContent.buttonVariant} onClick={handleConfirmAction} isLoading={isProcessing} disabled={(actionType === 'reject' || actionType === 'discrepant' || actionType === 'revert') && !comment.trim()} data-id="element-1863">
              {modalContent.buttonText}
            </Button>
          </div>
        </div>
      </Modal>
    </div>;
}